
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Load projects
  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('projects-api', {
        method: 'GET'
      });

      if (error) {
        console.error('Error loading projects:', error);
        toast.error('Failed to load projects');
        return;
      }

      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new project
  const createProject = async (name: string, description?: string) => {
    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('projects-api', {
        method: 'POST',
        body: { name, description }
      });

      if (error) {
        console.error('Error creating project:', error);
        toast.error('Failed to create project');
        return null;
      }

      const newProject = data.project;
      setProjects(prev => [newProject, ...prev]);
      toast.success(`Project "${name}" created successfully!`);
      return newProject;
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Set up real-time subscription for new projects
  useEffect(() => {
    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const channel = supabase
        .channel('projects-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'projects',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            const newProject = payload.new as Project;
            setProjects(prev => {
              // Check if project already exists (to avoid duplicates)
              if (prev.some(p => p.id === newProject.id)) {
                return prev;
              }
              return [newProject, ...prev];
            });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    setupSubscription();
  }, []);

  return {
    projects,
    isLoading,
    isCreating,
    loadProjects,
    refetch: loadProjects,
    createProject
  };
};
